import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { AlertCircle, ArrowLeft, CheckCircle2, KeyRound, MailCheck, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ title, description, children, testId }) => (
  <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center p-6">
    <div className="w-full max-w-md">
      <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0F2040] mb-4" data-testid={`${testId}-back`}><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
      <Card className="shadow-[0_16px_48px_rgb(15,32,64,0.12)]" data-testid={testId}>
        <CardHeader>
          <CardTitle className="text-[#0F2040] flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#4ABFCF]" /> {title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  </div>
);

/** /forgot-password — request a one-time reset link (works for Google-origin accounts too). */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Forgot your password?" testId="forgot-password-card"
      description="We'll email you a single-use link that works for 1 hour. If you normally sign in with Google, this also lets you set a password for email sign-in.">
      {sent ? (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="forgot-password-sent">
          <MailCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <p>If <b>{email}</b> has an account, a reset link is on its way. Check spam if it hasn't arrived in a couple of minutes.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="forgot-password-error"><AlertCircle className="w-4 h-4" /> {error}</div>}
          <div className="space-y-2">
            <Label htmlFor="fp-email">Work email</Label>
            <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11" data-testid="forgot-password-email" />
          </div>
          <Button type="submit" disabled={loading || !email} className="w-full h-11 bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white" data-testid="forgot-password-submit">{loading ? 'Sending…' : 'Email me a reset link'}</Button>
        </form>
      )}
    </Shell>
  );
}

/** /reset-password?token=… — complete the reset; signs out every device. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const valid = pw.length >= 10 && /\d/.test(pw) && /[A-Za-z]/.test(pw) && pw === confirm;
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const r = await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(r.data);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Choose a new password" testId="reset-password-card" description="At least 10 characters with a letter and a number. For safety, every signed-in device is signed out after the reset.">
      {!token && <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="reset-password-no-token">This link is missing its token. Request a new one from <Link to="/forgot-password" className="underline">Forgot password</Link>.</div>}
      {done ? (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="reset-password-done">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><p>{done.message}. Taking you to sign in…</p>
        </div>
      ) : token && (
        <form onSubmit={submit} className="space-y-4">
          {error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="reset-password-error"><AlertCircle className="w-4 h-4" /> {error}</div>}
          <div className="space-y-2"><Label htmlFor="rp-pw">New password</Label><Input id="rp-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" autoComplete="new-password" data-testid="reset-password-new" /></div>
          <div className="space-y-2"><Label htmlFor="rp-cf">Confirm password</Label><Input id="rp-cf" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-11" autoComplete="new-password" data-testid="reset-password-confirm" /></div>
          {pw && confirm && pw !== confirm && <p className="text-xs text-rose-600" data-testid="reset-password-mismatch">Passwords do not match.</p>}
          <Button type="submit" disabled={loading || !valid} className="w-full h-11 bg-[#0F2040] hover:bg-[#0F2040]/90 text-white" data-testid="reset-password-submit">{loading ? 'Saving…' : 'Set new password'}</Button>
        </form>
      )}
    </Shell>
  );
}


/** /trust-device?token=… — from the new-device alert email: mark that browser/OS as trusted. */
export function TrustDevicePage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ loading: !!token });
  useEffect(() => {
    if (!token) return;
    axios.post(`${API_URL}/api/auth/trust-device`, { token })
      .then((r) => setState({ done: r.data })).catch((e) => setState({ error: formatApiError(e) }));
  }, [token]);
  return (
    <Shell title="Trust this device" testId="trust-device-card" description="Trusted devices never trigger new sign-in alerts for your account. You can change this any time under Profile → Security.">
      {!token && <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm" data-testid="trust-device-no-token">This link is missing its token.</div>}
      {state.loading && <p className="text-sm text-slate-500">Confirming…</p>}
      {state.error && <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="trust-device-error"><AlertCircle className="w-4 h-4" /> {state.error}</div>}
      {state.done && <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="trust-device-done"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" /><p><b>{state.done.device}</b> is now a trusted device. Future sign-ins from it won't send alerts.</p></div>}
    </Shell>
  );
}
